import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  memo,
} from "react";
import "./products.css";
import { useNavigate } from "react-router-dom";
import { FaTimesCircle, FaSearch, FaChevronDown } from "react-icons/fa";
import {
  IoBookOutline,
  IoWarningOutline,
  IoTriangleOutline,
} from "react-icons/io5";
import { MdDoNotDisturb } from "react-icons/md";
import { useCurrency } from "../currency/currencyContext";

/* ------------------------------------------------------------------
   Inline SVG placeholder (much more reliable than via.placeholder.com)
   ------------------------------------------------------------------ */
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==";

/* ------------------------------------------------------------------
   Safe image URL builder — handles absolute, relative, empty & null
   ------------------------------------------------------------------ */
const BASE_URL = "https://backend.hira-tech.co.uk";

const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== "string") return PLACEHOLDER_IMAGE;

  const trimmed = imagePath.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;

  // Already a full URL (http/https) or data URI
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }

  // Relative path — normalize and prepend base URL
  const cleanPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${BASE_URL}${cleanPath}`;
};

/* ------------------------------------------------------------------
   Session-cached fetch helper
   ------------------------------------------------------------------ */
const fetchOrCache = async (url, cacheKey) => {
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      /* fall through */
    }
  }
  const res = await fetch(url);
  const data = await res.json();
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch {
    /* quota — ignore */
  }
  return data;
};

/* ------------------------------------------------------------------
   Custom Searchable Dropdown Component (unchanged)
   ------------------------------------------------------------------ */
const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  controlClassName,
  placeholderClassName,
  menuClassName,
  arrowClassName,
  arrowClosed,
  arrowOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const currentValue = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className={`searchable-dropdown ${className}`} ref={dropdownRef}>
      <div
        className={`${controlClassName} ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={placeholderClassName}>
          {currentValue ? currentValue.label : placeholder || "Select an Option"}
        </span>
        <span className={arrowClassName}>{isOpen ? arrowOpen : arrowClosed}</span>
      </div>
      {isOpen && (
        <div className={menuClassName}>
          <div className="searchable-dropdown-search">
            <FaSearch className="searchable-dropdown-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="searchable-dropdown-input"
            />
          </div>
          <div className="searchable-dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`searchable-dropdown-option ${
                    option.value === value ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="searchable-dropdown-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------
   Screw-matching logic (unchanged)
   ------------------------------------------------------------------ */
const SCREW_CATEGORY_BY_TYPE = {
  "Implant Level / Bone Level": "Screw Implant Level",
  "Tissue Level Implant": "Screw Tissue Level",
  "Abutment Level / Multiunit Level": "Screw Multiunit",
};

const SCANBODY_TIBASE_CATEGORIES = [
  "Scanbody Tissue Level",
  "Scanbody Implant level",
  "Scanbody Multiunit",
  "Ti-base Implant Level",
  "Ti-base Tissue Level",
];

const getMatchingScrew = (product, allProducts) => {
  if (!product || !Array.isArray(allProducts)) return null;

  const categoryName = product.product_category?.category_Name;
  if (!SCANBODY_TIBASE_CATEGORIES.includes(categoryName)) return null;

  const typeName = product.type?.type_name;
  const screwCategoryName = SCREW_CATEGORY_BY_TYPE[typeName];
  if (!screwCategoryName) return null;

  const brandId = product.product_brand?.brand_id;
  if (!brandId) return null;

  const candidates = allProducts.filter(
    (p) =>
      p.product_category?.category_Name === screwCategoryName &&
      p.product_brand?.brand_id === brandId
  );
  if (candidates.length === 0) return null;

  const nonRosen = candidates.filter(
    (p) => !p.product_variation?.variation_name?.toLowerCase().includes("rosen")
  );
  const pool = nonRosen.length > 0 ? nonRosen : candidates;

  const straight = pool.find((p) =>
    p.product_variation?.variation_name?.toLowerCase().includes("straight")
  );

  return straight || pool[0];
};

/* ------------------------------------------------------------------
   ProductCard (memoized) — with robust image loading
   ------------------------------------------------------------------ */
const ProductCard = memo(function ProductCard({
  product,
  isExpanded,
  onToggleExpand,
  onEdit,
  onOpenLabel,
  onPrintLabel,
  formatPrice,
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageSrc = useMemo(
    () =>
      imageError
        ? PLACEHOLDER_IMAGE
        : getImageUrl(product.imageGallery?.image1),
    [product.imageGallery?.image1, imageError]
  );

  return (
    <div className="admin-products-card">
      <div className="admin-products-card-image">
        <img
          src={imageSrc}
          alt={product.product_Name}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
          style={{
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
      <div className="admin-products-card-details">
        <h3 className="admin-products-card-title">{product.product_Name}</h3>
        <p className="admin-products-card-sku">SKU: {product.SKU}</p>
        <p
          className={`admin-products-card-description ${
            isExpanded ? "expanded" : "collapsed"
          }`}
        >
          {product.product_Description}
        </p>
        <span className="read-more-toggle" onClick={onToggleExpand}>
          {isExpanded ? "Read Less" : "Read More"}
        </span>
        <div className="admin-products-card-meta">
          <span className="admin-products-card-category">
            {product.product_category?.category_Name}
          </span>
          <span className="admin-products-card-price">
            {formatPrice(product.product_Price)}
          </span>
          <span className="admin-products-card-pricee">
            {product.product_Stock}
          </span>
          <span
            className={`admin-products-card-stock ${
              product.product_Stock > 0
                ? "admin-products-in-stock"
                : "admin-products-out-of-stock"
            }`}
          >
            {product.product_Stock > 0 ? "In Stock" : "Out of Stock"}
          </span>
        </div>
        <button className="admin-products-card-edit-btn" onClick={onEdit}>
          Edit Product
        </button>
        <div className="admin-products-label-buttons">
          <button
            className="admin-products-open-label-btn"
            onClick={onOpenLabel}
          >
            Open Label
          </button>
          <button
            className="admin-products-print-label-btn"
            onClick={onPrintLabel}
          >
            Print Label
          </button>
        </div>
      </div>
    </div>
  );
});

/* ==================================================================
   Main component
   ================================================================== */
function Products() {
  const navigate = useNavigate();
  const [productOrder, setProductOrder] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [implantCompanyOptions, setImplantCompanyOptions] = useState([]);
  const [selectedImplantCompany, setSelectedImplantCompany] = useState(null);
  const [variationOptions, setVariationOptions] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isOn, setIsOn] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const { formatPrice } = useCurrency();

  /* ---------- Debounce search ---------- */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  /* ---------- Matching screw for label modals ---------- */
  const matchingScrew = useMemo(
    () => getMatchingScrew(selectedProduct, productOrder),
    [selectedProduct, productOrder]
  );

  /* ---------- Fetch products ---------- */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          "https://backend.hira-tech.co.uk/product/getAllproduct/"
        );
        const data = await res.json();
        setProductOrder(data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  /* ---------- Fetch categories ---------- */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await fetchOrCache(
          "https://backend.hira-tech.co.uk/product/api/categories/",
          "cache_categories_v1"
        );
        setCategoryOptions([
          { value: null, label: "Select an Option" },
          ...data.map((cat) => ({
            value: cat.category_Name,
            label: cat.category_Name,
          })),
        ]);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  /* ---------- Fetch brands ---------- */
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const data = await fetchOrCache(
          "https://backend.hira-tech.co.uk/product/api/brands/",
          "cache_brands_v1"
        );
        setAllBrands(data);
      } catch (err) {
        console.error("Error fetching brands:", err);
      }
    };
    fetchBrands();
  }, []);

  /* ---------- Filter brand options by implant company ---------- */
  useEffect(() => {
    if (selectedImplantCompany && allBrands.length > 0) {
      const filtered = allBrands.filter(
        (brand) =>
          brand.implant_company?.company_name === selectedImplantCompany
      );
      setBrandOptions([
        { value: null, label: "Select an Option" },
        ...filtered.map((brand) => ({
          value: brand.brand_name,
          label: brand.brand_name,
        })),
      ]);
    } else {
      setBrandOptions([
        { value: null, label: "Select an Option" },
        ...allBrands.map((brand) => ({
          value: brand.brand_name,
          label: brand.brand_name,
        })),
      ]);
    }
  }, [selectedImplantCompany, allBrands]);

  /* ---------- Fetch implant companies ---------- */
  useEffect(() => {
    const fetchImplantCompanies = async () => {
      try {
        const data = await fetchOrCache(
          "https://backend.hira-tech.co.uk/product/api/implant-companies/",
          "cache_implant_companies_v1"
        );
        setImplantCompanyOptions([
          { value: null, label: "Select an Option" },
          ...data.map((comp) => ({
            value: comp.company_name,
            label: comp.company_name,
          })),
        ]);
      } catch (err) {
        console.error("Error fetching implant companies:", err);
      }
    };
    fetchImplantCompanies();
  }, []);

  /* ---------- Fetch variations (depends on category + type) ---------- */
  useEffect(() => {
    const fetchVariations = async () => {
      try {
        const data = await fetchOrCache(
          "https://backend.hira-tech.co.uk/product/api/categories/",
          "cache_categories_v1"
        );

        let matchedCategory = null;
        for (const category of data) {
          const hasMatchingType = category.types.some(
            (t) => t.type_name === selectedType
          );
          const isMatchingCategory =
            category.category_Name === selectedCategory;

          if (hasMatchingType && isMatchingCategory) {
            matchedCategory = category;
            break;
          }
        }

        const options = matchedCategory
          ? [
              { value: null, label: "Select an Option" },
              ...matchedCategory.var.map((v) => ({
                value: v.variation_name,
                label: v.variation_name,
              })),
            ]
          : [{ value: null, label: "No Variations Found" }];

        setVariationOptions(options);
      } catch (err) {
        console.error("Error fetching variations by type/category:", err);
      }
    };

    if (selectedType && selectedCategory) {
      fetchVariations();
    } else {
      setVariationOptions([{ value: null, label: "Select an Option" }]);
    }
  }, [selectedCategory, selectedType]);

  /* ---------- Fetch types ---------- */
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const data = await fetchOrCache(
          "https://backend.hira-tech.co.uk/product/api/type/",
          "cache_types_v1"
        );
        setTypeOptions([
          { value: null, label: "Select an Option" },
          ...data.map((t) => ({ value: t.type_name, label: t.type_name })),
        ]);
      } catch (err) {
        console.error("Error fetching types:", err);
      }
    };
    fetchTypes();
  }, []);

  /* ---------- Filtered products (memoized) ---------- */
  const filteredProducts = useMemo(() => {
    let filtered = productOrder;

    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => p.product_category?.category_Name === selectedCategory
      );
    }
    if (selectedBrand) {
      filtered = filtered.filter(
        (p) => p.product_brand?.brand_name === selectedBrand
      );
    }
    if (selectedImplantCompany) {
      filtered = filtered.filter(
        (p) => p.implant_company?.company_name === selectedImplantCompany
      );
    }
    if (selectedVariation) {
      filtered = filtered.filter(
        (p) => p.product_variation?.variation_name === selectedVariation
      );
    }
    if (selectedType) {
      filtered = filtered.filter((p) => p.type?.type_name === selectedType);
    }
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim().replace(/-/g, "");
      filtered = filtered.filter((p) => {
        const productName = p.product_Name?.toLowerCase() || "";
        const sku = p.SKU?.toLowerCase().replace(/-/g, "") || "";
        return productName.includes(query) || sku.includes(query);
      });
    }
    return filtered;
  }, [
    productOrder,
    selectedCategory,
    selectedBrand,
    selectedImplantCompany,
    selectedVariation,
    selectedType,
    debouncedSearch,
  ]);

  const showNoProductsAlert =
    filteredProducts.length === 0 &&
    (selectedCategory ||
      selectedBrand ||
      selectedImplantCompany ||
      selectedVariation ||
      selectedType ||
      searchQuery.trim());

  /* ---------- Callbacks ---------- */
  const handleEdit = useCallback(
    (productId) => {
      navigate(`/admin/product/edit/${productId}`);
    },
    [navigate]
  );

  const handleNew = useCallback(() => {
    navigate(`/admin/product/new`);
  }, [navigate]);

  const handleClearFilters = useCallback(() => {
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedImplantCompany(null);
    setSelectedVariation(null);
    setSelectedType(null);
    setSearchQuery("");
  }, []);

  const handleToggleExpand = useCallback(
    (index) => {
      setExpandedIndex((prev) => (prev === index ? null : index));
    },
    []
  );

  const handleOpenLabel = useCallback((product) => {
    setSelectedProduct(product);
    setIsOpen(true);
  }, []);

  const handlePrintLabel = useCallback((product) => {
    setSelectedProduct(product);
    setIsOn(true);
  }, []);

  /* ---------- Print label (unchanged logic) ---------- */
  const printLabel = (country) => {
    const labelElement = document.getElementById("print-label");
    const ukAddress = `02 Ulverscroft Road,<br /> Coventry, United Kingdom, CV3 5EZ`;
    const pkAddress = `House # 13, Street # 14, Sector C1 -<br /> Bahria Enclave, Islamabad`;

    const labelClone = labelElement.cloneNode(true);

    const addressElements = labelClone.getElementsByClassName("label-address");
    const madeInElements = labelClone.getElementsByClassName("made-in-text");

    for (let el of addressElements) {
      el.innerHTML = country === "uk" ? ukAddress : pkAddress;
    }

    for (let el of madeInElements) {
      el.textContent = `Made in ${country === "uk" ? "UK" : "PK"}`;
    }

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Label</title>
          <style>
            @page { size: 60mm 70mm; margin: 0; }
            body { display: flex; justify-content: center; align-items: center; height: 98vh; margin: 0; background-color: white; }
            #print-label { width: 60mm; height: 70mm; text-align: center; padding: 10px; }
          </style>
        </head>
        <body>
          <div id="print-label">${labelClone.innerHTML}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) return <div className="admin-products-loading">Loading....</div>;

  return (
    <div className="admin-products-page">
      <div className="admin-products-header">
        <h1 className="admin-products-title">Products List</h1>
        <button className="admin-products-new-btn" onClick={handleNew}>
          New Product
        </button>
      </div>

      {/* Search Bar */}
      <div className="admin-products-search-container">
        <div className="admin-products-search-bar">
          <FaSearch className="admin-products-search-icon" />
          <input
            type="text"
            placeholder="Search by product name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* No Products Found Alert */}
      {showNoProductsAlert && (
        <div className="admin-products-alert">
          No products found matching your filters. Please try different
          criteria.
        </div>
      )}

      <div className="admin-products-content">
        {/* Filters Section */}
        <div className="admin-products-filters">
          <div className="admin-products-filter-group">
            <label>Types</label>
            <SearchableDropdown
              options={typeOptions}
              value={selectedType}
              onChange={(opt) => setSelectedType(opt.value)}
              placeholder="Select Type"
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              arrowClosed={
                <FaChevronDown className="admin-products-dropdown-arrow-icon" />
              }
              arrowOpen={
                <FaChevronDown className="admin-products-dropdown-arrow-icon open" />
              }
            />
          </div>
          <div className="admin-products-filter-group">
            <label>Categories</label>
            <SearchableDropdown
              options={categoryOptions}
              value={selectedCategory}
              onChange={(opt) => setSelectedCategory(opt.value)}
              placeholder="Select Category"
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              arrowClosed={
                <FaChevronDown className="admin-products-dropdown-arrow-icon" />
              }
              arrowOpen={
                <FaChevronDown className="admin-products-dropdown-arrow-icon open" />
              }
            />
          </div>
          <div className="admin-products-filter-group">
            <label>Variations</label>
            <SearchableDropdown
              options={variationOptions}
              value={selectedVariation}
              onChange={(opt) => setSelectedVariation(opt.value)}
              placeholder="Select Variation"
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              arrowClosed={
                <FaChevronDown className="admin-products-dropdown-arrow-icon" />
              }
              arrowOpen={
                <FaChevronDown className="admin-products-dropdown-arrow-icon open" />
              }
            />
          </div>
          <div className="admin-products-filter-group">
            <label>Implant Companies</label>
            <SearchableDropdown
              options={implantCompanyOptions}
              value={selectedImplantCompany}
              onChange={(opt) => setSelectedImplantCompany(opt.value)}
              placeholder="Select Implant Company"
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              arrowClosed={
                <FaChevronDown className="admin-products-dropdown-arrow-icon" />
              }
              arrowOpen={
                <FaChevronDown className="admin-products-dropdown-arrow-icon open" />
              }
            />
          </div>
          <div className="admin-products-filter-group">
            <label>Implant Connection</label>
            <SearchableDropdown
              options={brandOptions}
              value={selectedBrand}
              onChange={(opt) => setSelectedBrand(opt.value)}
              placeholder="Select Brand"
              className="admin-products-dropdown"
              controlClassName="admin-products-dropdown-control"
              placeholderClassName="admin-products-dropdown-placeholder"
              menuClassName="admin-products-dropdown-menu"
              arrowClassName="admin-products-dropdown-arrow"
              arrowClosed={
                <FaChevronDown className="admin-products-dropdown-arrow-icon" />
              }
              arrowOpen={
                <FaChevronDown className="admin-products-dropdown-arrow-icon open" />
              }
            />
          </div>
          <button
            className="admin-products-clear-filters"
            onClick={handleClearFilters}
          >
            <FaTimesCircle style={{ marginRight: "5px" }} /> Clear All Filters
          </button>
        </div>

        {/* Products Grid */}
        <div className="admin-products-grid">
          {filteredProducts.map((product, index) => (
            <ProductCard
              key={product.product_Id ?? index}
              product={product}
              isExpanded={expandedIndex === index}
              onToggleExpand={() => handleToggleExpand(index)}
              onEdit={() => handleEdit(product.product_Id)}
              onOpenLabel={() => handleOpenLabel(product)}
              onPrintLabel={() => handlePrintLabel(product)}
              formatPrice={formatPrice}
            />
          ))}
        </div>
      </div>

      {/* Print Label Modal */}
      {isOn && selectedProduct && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setIsOn(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "500px",
              height: "100%",
              maxHeight: "500px",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              borderRadius: "8px",
              padding: "10px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              id="print-label"
              style={{
                width: "60mm",
                height: "70mm",
                backgroundColor: "white",
              }}
            >
              <div
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  fontFamily: "Arial Black",
                }}
              >
                {selectedProduct.product_Name}
              </div>
              <div style={{ textAlign: "right" }}>
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "0.6rem",
                    fontFamily: "Arial Black",
                  }}
                >
                  SKU: {selectedProduct.SKU}
                </span>
                {matchingScrew && (
                  <>
                    <br />
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: "0.5rem",
                        fontFamily: "Arial Black",
                      }}
                    >
                      Screw SKU: {matchingScrew.SKU}
                    </span>
                  </>
                )}
              </div>
              <hr
                style={{
                  border: "1px solid black",
                  marginTop: "5px",
                  marginBottom: "5px",
                }}
              />
              <div
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "0.6rem",
                  marginBottom: "40px",
                }}
              >
                <img
                  src="/label-icon/2.jpg"
                  alt="Factory"
                  style={{
                    width: "13px",
                    height: "13px",
                    marginRight: "3px",
                  }}
                />
                {new Date().toISOString().split("T")[0]}
              </div>
              <div
                style={{
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IoWarningOutline
                  style={{ width: "50px", height: "50px", marginLeft: "0" }}
                />
                <div style={{ position: "relative", display: "inline-block" }}>
                  <IoBookOutline size={50} style={{ marginLeft: "5px" }} />
                  <span
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "75%",
                      transform: "translate(-40%, -60%)",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                    }}
                  >
                    i
                  </span>
                </div>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <IoTriangleOutline size={50} style={{ marginLeft: "5px" }} />
                  <span
                    style={{
                      position: "absolute",
                      top: "60%",
                      left: "51%",
                      transform: "translate(-40%, -60%)",
                      fontSize: "0.4rem",
                      fontWeight: "bold",
                    }}
                  >
                    Non
                    <br />
                    Sterile
                  </span>
                </div>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <MdDoNotDisturb size={50} style={{ marginLeft: "5px" }} />
                  <span
                    style={{
                      position: "absolute",
                      top: "54%",
                      left: "50%",
                      transform: "translate(-40%, -60%)",
                      fontSize: "40px",
                      color: "black",
                    }}
                  >
                    2
                  </span>
                </div>
              </div>
              <hr style={{ border: "1px solid black", marginTop: "10px" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.6rem",
                  fontWeight: "bold",
                }}
              >
                <div
                  style={{
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <img
                    src="/label-icon/crowbar.jpg"
                    style={{
                      width: "15px",
                      height: "15px",
                      fontFamily: "Arial Black",
                    }}
                    alt="icon"
                  />
                  <span style={{ fontFamily: "Arial Black" }}>HIRA - TECH</span>
                </div>
              </div>
              <div
                className="label-address"
                style={{
                  textAlign: "left",
                  fontSize: "0.6rem",
                  fontWeight: "bold",
                  fontFamily: "Arial Black",
                }}
              >
                02 Ulverscroft Road,
                <br /> Coventry, United Kingdom, CV3 5EZ
              </div>
              <div style={{ textAlign: "right" }}>
                <span
                  className="made-in-text"
                  style={{
                    fontWeight: "bold",
                    fontSize: "0.6rem",
                    borderBottom: "solid black 1px",
                    display: "inline-block",
                    fontFamily: "Arial Black",
                  }}
                >
                  Made in UK
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "90px" }}>
              <button
                onClick={() => printLabel("uk")}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "white",
                  backgroundColor: "#007BFF",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  width: "150px",
                  textAlign: "center",
                }}
              >
                UK Order Label
              </button>
              <button
                onClick={() => printLabel("pk")}
                style={{
                  padding: "8px 12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "white",
                  backgroundColor: "#28a745",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  width: "150px",
                  textAlign: "center",
                }}
              >
                PK Order Label
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Label Modal */}
      {isOpen && selectedProduct && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setIsOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "500px",
              height: "100%",
              maxHeight: "500px",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            <div
              id="print-label"
              style={{
                width: "40mm",
                height: "20mm",
                backgroundColor: "white",
              }}
            >
              <div
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "0.5rem",
                  fontFamily: "Arial Black",
                  marginTop: "5px",
                }}
              >
                {selectedProduct.product_Name}
              </div>
              <div
                style={{
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: "0.5rem",
                  fontFamily: "Arial Black",
                }}
              >
                {selectedProduct.SKU}
              </div>
              {matchingScrew && (
                <div
                  style={{
                    textAlign: "left",
                    fontWeight: "bold",
                    fontSize: "0.45rem",
                    fontFamily: "Arial Black",
                  }}
                >
                  Screw: {matchingScrew.SKU}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                const label =
                  document.getElementById("print-label").innerHTML;
                const printWindow = window.open("", "_blank");
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>Print Label</title>
                      <style>
                        @page { size: 40mm 20mm; margin: 0; }
                        body { display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; background-color: white; }
                        #print-label { width: 40mm; height: 20mm; text-align: center; padding: 10px; }
                      </style>
                    </head>
                    <body>
                      <div id="print-label">${label}</div>
                      <script>
                        window.onload = function() {
                          window.print();
                          window.onafterprint = function() { window.close(); };
                        };
                      </script>
                    </body>
                  </html>
                `);
                printWindow.document.close();
              }}
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "white",
                backgroundColor: "#007BFF",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                width: "100%",
                maxWidth: "150px",
                textAlign: "center",
              }}
            >
              Print Label
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;